<template>
  <div class="checkout-page">
    <header class="checkout-nav">
      <button class="nav-back" type="button" :aria-label="$t('common.back')" @click="goBack">
        &lsaquo;
      </button>
      <h1>{{ $t('checkout.title') }}</h1>
      <div class="nav-spacer"></div>
    </header>

    <main class="checkout-main">
      <section class="pay-card">
        <div class="qris-brand" aria-label="QRIS">
          <span class="qris-mark">QRIS</span>
          <span class="qris-standard">{{ $t('checkout.qrisStandard') }}</span>
        </div>
        <h2>{{ $t('checkout.scanTitle') }}</h2>
        <p>{{ $t('checkout.scanDesc') }}</p>

        <div class="qris-receipt">
          <div class="qris-receipt-head">
            <span>{{ $t('checkout.qrisPay') }}</span>
            <strong>QRIS</strong>
          </div>

          <div class="qr-shell" :class="{ loading: loading }">
            <img
              v-if="qrImage"
              class="qr-image"
              :src="qrImage"
              :alt="$t('checkout.qrAlt')"
            />
            <div v-else class="qr-placeholder">
              <span v-if="loading" class="spinner"></span>
              <span v-else>{{ $t('checkout.qrUnavailable') }}</span>
            </div>
          </div>

          <div class="qris-hint">{{ $t('checkout.qrisHint') }}</div>
        </div>

        <div class="pay-status">
          <span class="status-dot"></span>
          <span>{{ statusText }}</span>
        </div>

        <div v-if="orderNo" class="order-line">
          <span>{{ $t('checkout.orderNo') }}</span>
          <strong>{{ orderNo }}</strong>
        </div>
      </section>
    </main>

    <footer class="checkout-footer">
      <button class="cancel-btn" type="button" @click="cancelPayment">
        {{ $t('checkout.cancelPayment') }}
      </button>
    </footer>
  </div>
</template>

<script>
import { Toast } from 'vant';
import { checkPayOrder, getPayInfoOrder } from '@/apis/recharge';
import QRCode from 'qrcode';
export default {
  name: 'CheckoutPage',
  data() {
    return {
      loading: false,
      checking: false,
      qrImage: '',
      orderNo: '',
      statusText: '',
      pollTimer: null
    };
  },
  mounted() {
    this.initCheckout();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async initCheckout() {
        const query = this.$route.query || {};

        this.orderNo = query.out_trade_no || query.order_no || query.trade_no || '';
        this.statusText = this.$t('checkout.waiting');

        this.qrImage = await this.pickQrValue(query);

        if (!this.qrImage) {
            this.loadQrCode();
        }

        if (this.orderNo) {
            this.startPolling();
        }
    },
    async pickQrValue(source = {}) {
        const value = source.qr_code ||
            source.qrcode ||
            source.qr ||
            source.code_url ||
            source.pay_qr ||
            source.pay_url ||
            '';

        return await this.formatQrImage(value);
    },
    async formatQrImage(value) {
        if (!value) return '';

        const text = String(value).trim();

        const isImageUrl = /^data:image\//i.test(text) ||
            /^(https?:)?\/\/.+\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i.test(text) ||
            /^\/.+\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i.test(text);

        if (isImageUrl) return text;

        if (/^[A-Za-z0-9+/]+={0,2}$/.test(text) && text.length > 100) {
            return `data:image/png;base64,${text}`;
        }

        return await QRCode.toDataURL(text, {
            width: 260,
            margin: 1,
            errorCorrectionLevel: 'M'
        });
    },
    async loadQrCode() {
        if (!this.orderNo || this.loading) return;

        this.loading = true;

        try {
            const res = await getPayInfoOrder({
            order_no: this.orderNo
            });

            if (res && res.code === 1) {
            const data = res.data || {};
            this.qrImage = await this.pickQrValue(data);
            }

            if (!this.qrImage) {
            this.statusText = this.$t('checkout.qrUnavailable');
            }
        } catch (err) {
            console.error('getPayInfoOrder error:', err);
            this.statusText = this.$t('checkout.qrUnavailable');
        } finally {
            this.loading = false;
        }
    },
    startPolling() {
      this.stopPolling();
      this.checkOrderStatus();
      this.pollTimer = window.setInterval(this.checkOrderStatus, 3000);
    },
    stopPolling() {
      if (!this.pollTimer) return;
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    async checkOrderStatus() {
      if (!this.orderNo || this.checking) return;

      this.checking = true;

      try {
        const res = await checkPayOrder({
          order_no: this.orderNo
        });

        if (!res || res.code !== 1) return;

        const data = res.data || {};
        const paid = data.is_pay == 1 ||
          data.pay_status == 1 ||
          data.status == 1 ||
          data.status === 'paid' ||
          data.status === 'success';

        if (paid) {
          this.stopPolling();
          this.statusText = this.$t('checkout.paid');
          Toast(this.$t('checkout.paid'));
          setTimeout(() => {
            this.$router.replace('/user/member/member');
          }, 800);
        }
      } catch (err) {
        console.error('checkPayOrder error:', err);
      } finally {
        this.checking = false;
      }
    },
    cancelPayment() {
      this.stopPolling();
      Toast(this.$t('checkout.cancelled'));
      this.goBack();
    },
    goBack() {
      this.$router.back();
    }
  }
};
</script>

<style scoped>
.checkout-page {
  width: 100%;
  max-width: 450px;
  min-height: 100vh;
  margin: 0 auto;
  background:
    linear-gradient(180deg, #fff 0%, #f5f5f7 34%, #f5f5f7 100%);
  color: #1d1d1f;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif;
  display: flex;
  flex-direction: column;
}

.checkout-nav {
  position: sticky;
  top: 0;
  z-index: 10;
  height: 52px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.86);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: saturate(180%) blur(18px);
  -webkit-backdrop-filter: saturate(180%) blur(18px);
  box-sizing: border-box;
}

.checkout-nav h1 {
  margin: 0;
  font-size: 17px;
  font-weight: 650;
}

.nav-back,
.nav-spacer {
  width: 36px;
  height: 36px;
}

.nav-back {
  padding: 0 0 4px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #d71920;
  font-size: 38px;
  font-weight: 300;
  line-height: 32px;
}

.nav-back:active {
  background: rgba(215, 25, 32, 0.08);
}

.checkout-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 18px 116px;
  box-sizing: border-box;
}

.pay-card {
  width: 100%;
  padding: 24px 18px 22px;
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.08);
  text-align: center;
  box-sizing: border-box;
}

.qris-brand {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  height: 34px;
  margin: 0 auto 18px;
  padding: 0 11px 0 6px;
  border-radius: 999px;
  background: #f5f5f7;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.qris-mark {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  background: #d71920;
  color: #fff;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0;
}

.qris-standard {
  color: #1d1d1f;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: uppercase;
}

.pay-card h2 {
  margin: 0;
  font-size: 23px;
  font-weight: 750;
  line-height: 1.18;
}

.pay-card p {
  max-width: 280px;
  margin: 8px auto 22px;
  color: #6e6e73;
  font-size: 14px;
  line-height: 1.45;
}

.qris-receipt {
  width: 100%;
  max-width: 318px;
  margin: 0 auto;
  padding: 12px 12px 14px;
  border-radius: 22px;
  background: #fff;
  border: 1px solid #e5e5ea;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
}

.qris-receipt-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 30px;
  margin-bottom: 10px;
  padding: 0 2px;
  color: #6e6e73;
  font-size: 12px;
  font-weight: 650;
}

.qris-receipt-head strong {
  color: #d71920;
  font-size: 15px;
  font-weight: 900;
  letter-spacing: 0;
}

.qr-shell {
  width: 100%;
  aspect-ratio: 1;
  margin: 0 auto;
  padding: 10px;
  border-radius: 18px;
  background: #fafafa;
  border: 2px solid #1d1d1f;
  box-sizing: border-box;
}

.qr-image,
.qr-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 14px;
}

.qr-image {
  display: block;
  object-fit: contain;
}

.qr-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: #f5f5f7;
  color: #8e8e93;
  font-size: 13px;
  line-height: 1.35;
  box-sizing: border-box;
}

.qris-hint {
  margin-top: 10px;
  color: #6e6e73;
  font-size: 12px;
  font-weight: 550;
  line-height: 1.35;
}

.spinner {
  width: 26px;
  height: 26px;
  border: 2px solid rgba(215, 25, 32, 0.22);
  border-top-color: #d71920;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.pay-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 28px;
  margin-top: 20px;
  padding: 0 12px;
  border-radius: 999px;
  background: #fff4f4;
  color: #3a3a3c;
  font-size: 13px;
  font-weight: 600;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #d71920;
  box-shadow: 0 0 0 4px rgba(215, 25, 32, 0.14);
}

.order-line {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  color: #8e8e93;
  font-size: 12px;
}

.order-line strong {
  max-width: 180px;
  overflow: hidden;
  color: #6e6e73;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.checkout-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  padding: 12px 18px calc(12px + env(safe-area-inset-bottom));
  background: rgba(245, 245, 247, 0.92);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: saturate(180%) blur(18px);
  -webkit-backdrop-filter: saturate(180%) blur(18px);
  box-sizing: border-box;
}

.cancel-btn {
  display: block;
  width: 100%;
  max-width: 414px;
  height: 52px;
  margin: 0 auto;
  border: 0;
  border-radius: 16px;
  background: #fff;
  color: #ff3b30;
  font: inherit;
  font-size: 16px;
  font-weight: 650;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.cancel-btn:active {
  background: #fff1f0;
  transform: scale(0.99);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
